import { Component, Inject, OnInit, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { AssetDto } from '../../../../../api/src/asset/models/asset.dto';
import { ImagePickerActionsDto } from '../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { Entity } from '../../_core/state/entities.model';
import { CropperPosition, ImageCroppedEvent } from '../components/image-cropper/interfaces';
import { isArray } from 'class-validator';
import { AssetService, FileUploadObject } from '../../_core/state/asset/asset.service';
import { Type } from '../../../../../api/src/asset/asset.entity';
import { ProjectsQuery } from '../../_core/state/project';
import { BusinessQuery } from '../../_core/state/business';
import { environment } from '../../../environments/environment';
import { HttpEventType } from '@angular/common/http';
import { PhotopeaIframeComponent } from '../components/photopea-iframe/photopea-iframe.component';


export type ImageEditorMode = 'default' | 'focal-area-cropping' | 'focal-area-select';

export interface CreativeUnitDimension {
	name: string;    // e.g., "300x250 Banner", "Instagram Story"
	width: number;   // Pixel width
	height: number;  // Pixel height
}

export interface ImageEditorDialogData {
	asset?: AssetDto;
	config: ImagePickerActionsDto;
	mode?: ImageEditorMode;
	selectMode?: boolean; // Flag to indicate focal area selection mode
	initialTargetCrop?: { x: number; y: number; width: number; height: number }; // Existing targetCrop to match/select
	title?: string; // Optional custom title for the dialog
	creativeUnits?: CreativeUnitDimension[]; // Optional creative units for auto-generation
}

@Component({
	selector: 'app-image-editor-dialog',
	templateUrl: './image-editor-dialog.component.html',
	styleUrls: ['./image-editor-dialog.component.scss']
})
export class ImageEditorDialogComponent implements OnInit {

	@ViewChild(PhotopeaIframeComponent) photopeaComponent: PhotopeaIframeComponent;

	private activeAssetSubject = new BehaviorSubject<Partial<AssetDto>>(null);
	public activeAsset$ = this.activeAssetSubject.asObservable();

	private activeAssetVariationsSubject = new BehaviorSubject<Partial<AssetDto>[]>(null);
	public activeAssetVariations$ = this.activeAssetVariationsSubject.asObservable();
	public activeToolOption: Entity;

	public undoStack: any[] = [];
    public redoStack: any[] = [];

	public loadingMessage: string | null = null;
	public filteredToolOptions: Entity[] = [];

	public initialTargetCrop: CropperPosition;
	public targetCropIsDirty = false;

	public originalConfig: ImagePickerActionsDto;
	public currentMode: ImageEditorMode = 'default';
	private didUserCrop = false; // Track if user performed a crop action

	constructor(
		public dialogRef: MatDialogRef<ImageEditorDialogComponent>,
		public assetService: AssetService,
		@Inject(MAT_DIALOG_DATA) public data: ImageEditorDialogData
	) {}

	ngOnInit(): void {
		
		// Set mode from data or default
		this.currentMode = this.data.mode || 'default';


        if (this.data.asset) {

			this.originalConfig = { ...this.data.config };

            this.activeAssetSubject.next(this.data.asset);

            this.undoStack.push(this.data.asset);
        }
    }

	isFocalAreaMode(): boolean {
		return this.currentMode === 'focal-area-cropping' || this.currentMode === 'focal-area-select';
	}

	isSelectMode(): boolean {
		return this.data.selectMode === true;
	}

    skip() {
        this.dialogRef.close();
    }

    setActiveAsset(asset: any) {
    	console.log('Setting active asset:', asset);
    	this.undoStack.push(asset);
        this.redoStack = [];
        this.activeAssetSubject.next(asset);
    }

	setActiveVariations(variations: Partial<AssetDto>[]) {
		console.log('Setting active variations:', variations);
		const currentVariations = this.activeAssetVariationsSubject.value || [];
		this.activeAssetVariationsSubject.next([...variations, ...currentVariations]);

		if (variations.length) {
			this.setActiveAsset(variations[0]);
		}
	}

    undo() {
        if (this.undoStack.length > 1) {
            this.redoStack.push(this.undoStack.pop());
            this.activeAssetSubject.next(this.undoStack[this.undoStack.length - 1]);
        }
    }

    redo() {
        if (this.redoStack.length) {
            this.undoStack.push(this.redoStack.pop());
            this.activeAssetSubject.next(this.undoStack[this.undoStack.length - 1]);
        }
    }

	reset() {
		this.activeAssetSubject.next(this.undoStack[0]);
		this.undoStack = [this.undoStack[0]];
		this.redoStack = [];
	}

	onImageCrop(ev: ImageCroppedEvent) {
		this.data = {
			...this.data,
			config: {
				...this.data.config,
				targetCropConfig: {
					...this.data.config.targetCropConfig,
					target: {
						x: ev.imagePosition.x1,
						y: ev.imagePosition.y1,
						width: ev.imagePosition.x2 - ev.imagePosition.x1,
						height: ev.imagePosition.y2 - ev.imagePosition.y1
					}
				}
			}
		}

		this.targetCropIsDirty = true;
		this.didUserCrop = true; // Mark that user performed a crop
	}

	setConfigValue(config: ImagePickerActionsDto) {

		if (config?.targetCropConfig?.target?.x > -1) {
			this.initialTargetCrop = {
				x1: -100,
				y1: -100,
				x2: 10000,
				y2: 10000
			};

			if (config?.targetCropConfig?.target?.x > -1) {
				this.initialTargetCrop.x1 = config?.targetCropConfig?.target?.x;
			}
			if (config?.targetCropConfig?.target?.y > -1) {
				this.initialTargetCrop.y1 = config?.targetCropConfig?.target?.y;
			}
			if (config?.targetCropConfig?.target?.width > -1) {
				this.initialTargetCrop.x2 = this.initialTargetCrop.x1 + config?.targetCropConfig?.target?.width;
			}
			if (config?.targetCropConfig?.target?.height > -1) {
				this.initialTargetCrop.y2 = this.initialTargetCrop.y1 + config?.targetCropConfig?.target?.height;
			}
		}

	}

	toggleSidebar() {
		this.data.config.minimizeSidebar = !this.data.config.minimizeSidebar;
	}

	async save() {
		// If Photopea is active and not already saving, trigger its save first
		if (this.activeToolOption?.id === 'photopea-iframe' && this.photopeaComponent && !this.photopeaComponent.isSaving) {
			console.log('Triggering Photopea save...');
			this.photopeaComponent.triggerSave();
			// The actual save will happen via the saveImage event handler
			// which calls setActiveAsset and then save() again
			return;
		}

		console.log('Saving activeAsset:', this.activeAssetSubject.value);
		let result: Partial<AssetDto> & { target?: ImagePickerActionsDto['targetCropConfig']['target'] } = this.activeAssetSubject.value;
		if (isArray(result.assetPath)) {
			result.assetPath = result.assetPath[0];
		}

		if (this.data.config.targetCropConfig && (this.data.config.targetCropConfig.target?.x > -1)) {
			result = {
				...result,
				target: this.data.config.targetCropConfig.target,
				// Save the maintainAspectRatio state so it persists
				maintainAspectRatio: this.data.config.targetCropConfig.maintainAspectRatio,
				currentMode: this.currentMode,
				didUserCrop: this.didUserCrop
			};
		} else {
			result = {
				...result,
				currentMode: this.currentMode,
				didUserCrop: this.didUserCrop
			};
		}
		console.log('Closing dialog with result:', result);
		this.dialogRef.close(result);
	}

	onFocalAreaSelected(event: { focalArea: any; imageNaturalWidth: number; imageNaturalHeight: number }): void {
		// Convert percentage values to floats (0-1 range)
		const { focalArea } = event;

		const result = {
			...this.activeAssetSubject.value,
			target: {
				x: focalArea.boundingBox.x / 100,
				y: focalArea.boundingBox.y / 100,
				width: focalArea.boundingBox.width / 100,
				height: focalArea.boundingBox.height / 100,
				cropMode: focalArea.cropMode || 'contain',
				aspectPriority: focalArea.aspectPriority // Pass through aspectPriority (defaults to 'target' in API if not set)
			},
			currentMode: this.currentMode,
			didUserCrop: true // Focal area selection is a crop action
		};

		this.dialogRef.close(result);
	}
	setImagesize(event:any){
		if(event.transformed.size.height){
			this.data = {
				...this.data,
				config: {
					...this.data.config,
					targetCropConfig: {
						...this.data.config.targetCropConfig,
						imageHeight : event.transformed.size.height,
						imageWidth:event.transformed.size.width,
					}
				}
			}
			this.originalConfig = {...this.data.config};
		}
	}

	onFilteredToolsChanged(tools: Entity[]): void {
		this.filteredToolOptions = tools;
	}

	setActiveTool(tool: Entity): void {
		this.activeToolOption = tool;

		// Auto-hide sidebar for full-screen tools
		if (tool.id === 'target-crop' || tool.id === 'bria-iframe' || tool.id === 'photopea-iframe') {
			this.data.config.minimizeSidebar = true;
		} else {
			this.data.config.minimizeSidebar = false;
		}
	}

}
